import { useAlert } from "@components/alert/withAlert";
import BusyIndicator from "@components/busyIndicator";
import { getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";
import { DownloadIcon, ExpandIcon } from "@components/icon";
import SectionSwitch from "@components/splitWizard/SectionSwitch";
import { IColumn, IRow } from "@components/table";
import TableWithAutoSizedColumns from "@components/table/TableWithAutoSizedColumns";
import {
    EntitySetName,
    EntityTypeName,
    IPrAnnualTaxSettlementEntity,
    IPrAnnualTaxSettlementRequestEntity,
    PrAnnualTaxSettlementEntity,
    PrAnnualTaxSettlementRequestEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrAnnualTaxSettlementRequestResultTypeCode,
    PrAnnualTaxSettlementRequestStatusTypeCode,
    PrAnnualTaxSettlementStatusCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import {
    getThemeColorForSettlementStatus,
    getThemeColorForStatus
} from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequest.def";
import {
    GENERATE_ADVANCED_TAX_RETURN_URL,
    GENERATE_ADVANCED_TAX_RETURN_XML,
    GENERATE_WITHHOLDING_TAX_RETURN_URL,
    GENERATE_WITHHOLDING_TAX_RETURN_XML,
    ISectionProps
} from "@pages/payroll/payrollYearOperations/PayrollYearOperation.utils";
import {
    AlertWrapper,
    ClickableStyled,
    DataGrid,
    DataGridCell,
    LinksWrapper,
    Subtitle,
    TableWrapper,
    Text
} from "@pages/payroll/payrollYearOperations/screens/PayrollYearOperationScreen.styles";
import customFetch from "@utils/customFetch";
import { downloadFileFromUrl } from "@utils/FileStorage";
import { logger } from "@utils/log";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AppContext } from "../../../../contexts/appContext/AppContext.types";
import { IconSize, Status } from "../../../../enums";
import { ColoredText } from "../../../../global.style";
import { ROUTE_ANNUAL_TAX_SETTLEMENT, ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST } from "../../../../routes";
import { getAlertFromError } from "../../../../views/formView/Form.utils";

interface IAnnualTaxStatementRows {
    request: IRow[];
    statement: IRow[];
    requestsResultCounts: Record<PrAnnualTaxSettlementRequestResultTypeCode, number>;
}

function mergeRequests(requests: IPrAnnualTaxSettlementRequestEntity[], mergeBy: "StatusCode" | "ResultCode"): IPrAnnualTaxSettlementRequestEntity[] {
    return requests.reduce((acc, curr) => {
        const existing = acc.find(item => item[mergeBy] === curr[mergeBy]);
        if (existing) {
            existing._metadata[""].count += curr._metadata[""].count;
        } else {
            acc.push(curr);
        }
        return acc;
    }, [] as IPrAnnualTaxSettlementRequestEntity[]);
}

const AnnualTaxStatement: React.FC<ISectionProps> = ({
                                                         task,
                                                         year,
                                                         onCheck,
                                                         storage
                                                     }) => {
    const { t } = useTranslation([
        "PayrollYearOperation",
        getEnumNameSpaceName(EntityTypeName.PrAnnualTaxSettlementRequestStatusType),
        getEnumNameSpaceName(EntityTypeName.PrAnnualTaxSettlementRequestResultType),
        getEnumNameSpaceName(EntityTypeName.PrAnnualTaxSettlementStatus)
    ]);
    const { alert, setAlert } = useAlert({});
    const [rows, setRows] = useState<IAnnualTaxStatementRows>(null);
    const [isBusy, setBusy] = React.useState(false);
    const context = useContext(AppContext);

    useEffect((): void => {
        try {
            const newRows = {} as IAnnualTaxStatementRows;
            const res = storage.oData.getEntitySetWrapper(EntitySetName.PrAnnualTaxSettlementRequests)
                    .query()
                    .filter(`${PrAnnualTaxSettlementRequestEntity.Year} eq ${year}`)
                    .groupBy(PrAnnualTaxSettlementRequestEntity.ResultCode, PrAnnualTaxSettlementRequestEntity.StatusCode)
                    .aggregate("$count as @odata.count")
                    .fetchData<IPrAnnualTaxSettlementRequestEntity[]>();

            const resStatement = storage.oData.getEntitySetWrapper(EntitySetName.PrAnnualTaxSettlements)
                .query()
                .filter(`${PrAnnualTaxSettlementEntity.Year} eq ${year}`)
                .groupBy(PrAnnualTaxSettlementEntity.StatusCode)
                .aggregate("$count as @odata.count")
                .fetchData<IPrAnnualTaxSettlementEntity[]>();

            res.then((response) => {
                const requests = response.value;

                newRows.requestsResultCounts = mergeRequests(requests, "ResultCode").reduce((acc, curr) => {
                    acc[curr.ResultCode] = curr._metadata[""].count ?? 0;
                    return acc;
                }, {} as Record<PrAnnualTaxSettlementRequestResultTypeCode, number>);

                for (const resultCode of Object.values(PrAnnualTaxSettlementRequestResultTypeCode)) {
                    if (!newRows.requestsResultCounts[resultCode]) {
                        newRows.requestsResultCounts[resultCode] = 0;
                    }
                }

                const mergedRequestsByStatus = mergeRequests(requests, "StatusCode");
                newRows.request = mergedRequestsByStatus.filter((item) => {
                    return item.StatusCode !== PrAnnualTaxSettlementRequestStatusTypeCode.NotRequested;
                }).map((item) => {
                    const statusName = getEnumDisplayValue(EntityTypeName.PrAnnualTaxSettlementRequestStatusType, item.StatusCode);
                    return {
                        id: item.StatusCode,
                        values: {
                            [PrAnnualTaxSettlementRequestEntity.Status]: {
                                tooltip: statusName,
                                value: <ColoredText
                                    color={getThemeColorForStatus(item.StatusCode as PrAnnualTaxSettlementRequestStatusTypeCode)}>
                                    {statusName}
                                </ColoredText>
                            },
                            count: getTableDrillDownLink((item._metadata[""].count ?? 0).toString(), {
                                route: ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST,
                                context: context,
                                filters: {
                                    [PrAnnualTaxSettlementRequestEntity.Status]: item.StatusCode,
                                    [PrAnnualTaxSettlementRequestEntity.Year]: year
                                }
                            })
                        }
                    };
                });
            });

            resStatement.then((responseStatement) => {
                const statements = responseStatement.value;
                newRows.statement = statements.map((item) => {
                    const statusName = getEnumDisplayValue(EntityTypeName.PrAnnualTaxSettlementStatus, item.StatusCode);
                    return {
                        id: item.StatusCode,
                        values: {
                            Status: {
                                tooltip: statusName,
                                value: <ColoredText
                                    color={getThemeColorForSettlementStatus(item.StatusCode as PrAnnualTaxSettlementStatusCode)}>
                                    {statusName}
                                </ColoredText>
                            },
                            count: getTableDrillDownLink((item._metadata[""].count ?? 0).toString(), {
                                route: ROUTE_ANNUAL_TAX_SETTLEMENT,
                                context: context,
                                filters: {
                                    [PrAnnualTaxSettlementEntity.Status]: item.StatusCode,
                                    [PrAnnualTaxSettlementEntity.Year]: year
                                }
                            })
                        }
                    };
                });
            });

            // wait for both promises to resolve before setting the rows
            Promise.all([res, resStatement]).then(() => {
                setRows(newRows);
            });
        } catch (e) {
            setAlert(getAlertFromError(e));
            logger.error("Error while fetching Taxpayer Declarations", e);
        }

    }, [year, storage.oData, t, context, setAlert]);

    const handleCheck = useCallback(async (checked: boolean) => {
        const alertProps = await onCheck(checked);
        setAlert(alertProps);
    }, [onCheck, setAlert]);

    const downloadTaxFile = useCallback(
        async (url: string, fileName: string) => {
            await downloadFileFromUrl(url.replace("{year}", year), {
                setBusy,
                setAlert
            });
        }, [year, setAlert, setBusy]
    );

    const handleAdvancedTaxFileDownload = useCallback(async () => {
        await downloadTaxFile(
            GENERATE_ADVANCED_TAX_RETURN_XML,
            t("PayrollYearOperation:AdvancedAndWithholdingTaxSettlement.AdvanceTax")
        );
    }, [downloadTaxFile, t]);

    const handleWithholdingTaxFileDownload = useCallback(async () => {
        await downloadTaxFile(
            GENERATE_WITHHOLDING_TAX_RETURN_XML,
            t("PayrollYearOperation:AdvancedAndWithholdingTaxSettlement.WithholdingTax")
        );
    }, [downloadTaxFile, t]);

    const columnsRequest: IColumn[] = useMemo(() => {
        return [{
            id: PrAnnualTaxSettlementRequestEntity.Status,
            label: t("PayrollYearOperation:AnnualTaxStatement.Status"),
        }, {
            id: "count",
            label: t("PayrollYearOperation:Detail.Count"),
        }];
    }, [t]);

    const columns: IColumn[] = useMemo(() => {
        return [{
            id: "Status",
            label: t("PayrollYearOperation:AnnualTaxStatement.StatementState"),
        }, {
            id: "count",
            label: t("PayrollYearOperation:Detail.Count"),
        }];
    }, [t]);

    const handleRedirect = useCallback(async (urlTemplate: string) => {
        const url = urlTemplate.replace("{year}", year);
        const res = await customFetch(url);
        const value = await res.text();

        if (res.ok) {
            // open in new tab
            window.open(value, "_blank");
        } else {
            setAlert({
                status: Status.Error,
                title: t("PayrollYearOperation:AnnualTaxStatement.URLGenerationFailed")
            });
        }
    }, [year, setAlert, t]);

    const handleWithholdingTaxRedirect = useCallback(async () => {
        await handleRedirect(GENERATE_WITHHOLDING_TAX_RETURN_URL);
    }, [handleRedirect]);


    const handleAdvanceTaxRedirect = useCallback(async () => {
        await handleRedirect(GENERATE_ADVANCED_TAX_RETURN_URL);
    }, [handleRedirect]);

    return (<>
        {isBusy && <BusyIndicator/>}
        <AlertWrapper>{alert}</AlertWrapper>
        <Text>{t("PayrollYearOperation:AnnualTaxStatement.AllRequestsOverview")}</Text>
        <DataGrid columns={3}>
            {Object.entries(rows?.requestsResultCounts ?? {}).map(([resultCode]) => {
                const resultName = getEnumDisplayValue(EntityTypeName.PrAnnualTaxSettlementRequestResultType, resultCode as PrAnnualTaxSettlementRequestResultTypeCode);
                return (<DataGridCell key={resultCode} $isBold>{resultName + ":"}</DataGridCell>);
            })}
            {Object.entries(rows?.requestsResultCounts ?? {}).map(([resultCode, count]) => {
                return (<DataGridCell key={resultCode + "count"}>{
                    count === 0 ? (count ?? 0).toString() :
                    getTableDrillDownLink((count ?? 0).toString(), {
                        route: ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST,
                        context: context,
                        filters: {
                            [PrAnnualTaxSettlementRequestEntity.Result]: resultCode,
                            [PrAnnualTaxSettlementRequestEntity.Year]: year
                        }
                    }).value
                }</DataGridCell>);
            })}
        </DataGrid>
        {!!rows?.request?.length && <>
            <Text>{t("PayrollYearOperation:AnnualTaxStatement.RequestedOverview")}</Text>
            <TableWrapper>
                <TableWithAutoSizedColumns
                        rows={rows.request}
                        columns={columnsRequest}
                        disableSort={true}
                        disableVirtualization={true}/>
            </TableWrapper>
            <ClickableStyled link={ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST}
                             isLink>{t("PayrollYearOperation:AnnualTaxStatement.GoToAnnualTaxStatementRequest")}</ClickableStyled>
        </>}
        {!!rows?.statement?.length && <>
            <Text>{t("PayrollYearOperation:AnnualTaxStatement.StatementsOverview")}</Text>
            <TableWrapper>
                <TableWithAutoSizedColumns
                        rows={rows.statement}
                        columns={columns}
                        disableSort={true}
                        disableVirtualization={true}/>
            </TableWrapper>
            <ClickableStyled link={ROUTE_ANNUAL_TAX_SETTLEMENT}
                             isLink>{t("PayrollYearOperation:AnnualTaxStatement.GoToAnnualTaxStatement")}</ClickableStyled>
        </>}
        <Subtitle>{t("PayrollYearOperation:AnnualTaxStatement.AdvanceTax")}</Subtitle>
        <LinksWrapper>
            <ClickableStyled onClick={handleAdvancedTaxFileDownload} isLink>
                {t("PayrollYearOperation:Detail.DownloadXML")}
                <DownloadIcon width={IconSize.XS} height={IconSize.XS}/>
            </ClickableStyled>
            <ClickableStyled onClick={handleAdvanceTaxRedirect} isLink $isBig={true}>
                {t("PayrollYearOperation:AnnualTaxStatement.AdvanceTaxLink")}
                <ExpandIcon width={IconSize.XS} height={IconSize.XS}/>
            </ClickableStyled>
        </LinksWrapper>
        <Subtitle>{t("PayrollYearOperation:AnnualTaxStatement.WithholdingTax")}</Subtitle>
        <LinksWrapper>
            <ClickableStyled onClick={handleWithholdingTaxFileDownload} isLink>
                {t("PayrollYearOperation:Detail.DownloadXML")}
                <DownloadIcon width={IconSize.XS} height={IconSize.XS}/>
            </ClickableStyled>
            <ClickableStyled onClick={handleWithholdingTaxRedirect} isLink $isBig={true}>
                {t("PayrollYearOperation:AnnualTaxStatement.WithholdingTaxLink")}
                <ExpandIcon width={IconSize.XS} height={IconSize.XS}/>
            </ClickableStyled>
        </LinksWrapper>
        <SectionSwitch
                isChecked={task.IsChecked}
                onCheck={handleCheck}/>
    </>);
};

export default AnnualTaxStatement;